import {
  SimpleGrid,
  Container,
  Center,
  Heading,
  Button,
  Box,
  useBoolean,
  Text,
} from "@chakra-ui/react";
import BoldSpinner from "components/BoldSpinner";
import useAssets from "hooks/useAssets";
import useTranslate from "hooks/useTranslate";
import { useCallback, useEffect, useMemo, useState } from "react";
import V1Card from "components/V1Card";
import { v1Name, v1OpenSeaUrl, v2Name } from "config";
import EmptyNftCardWithLink from "components/EmptyNftCardWithLink";
import MintSuccessPopup from "components/MintSuccessPopup";
import useSale from "hooks/useSale";
import useV2s from "hooks/useV2s";
import Panel from "components/Panel";
import { formatEtherBalance } from "utils/numberUtils";
import TermsConsentPopup from "components/TermsConsentPopup";
import useV2Mint from "hooks/useV2Mint";
import pluralize, { plural } from "pluralize";
import useConnectedAccount from "hooks/useConnectedAccount";

const V1sContainer = () => {
  const translate = useTranslate();
  const { price } = useSale();
  const [address] = useConnectedAccount();
  const { v1sInWallet, fancyBearsInWallet } = useAssets();
  const { allV2Ids } = useV2s();
  const [isMintSuccessPopupOpen, setIsMintSuccessPopupOpen] = useBoolean();
  const [isTermsConsentPopupOpen, setIsTermsConsentPopupOpen] = useBoolean();
  const [tokenIds, setTokenIds] = useState<number[]>();

  const totalPrice = useMemo(() => {
    if (tokenIds?.length && price && fancyBearsInWallet) {
      const total = price.mul(tokenIds.length);
      return fancyBearsInWallet.length ? total.div(2) : total;
    }
  }, [price, tokenIds, fancyBearsInWallet]);

  const handleMintSuccess = useCallback(() => {
    setIsMintSuccessPopupOpen.on();
  }, [setIsMintSuccessPopupOpen]);

  const [mint, isMinting] = useV2Mint(totalPrice, tokenIds, handleMintSuccess);

  const handleReadTerms = useCallback(() => {
    setIsTermsConsentPopupOpen.on();
  }, [setIsTermsConsentPopupOpen]);

  const handleTermsConsentSuccess = useCallback(() => {
    setIsTermsConsentPopupOpen.off();
    mint();
  }, [mint, setIsTermsConsentPopupOpen]);

  useEffect(() => {
    if (v1sInWallet?.length && !tokenIds) {
      const availableBearsToClaim = v1sInWallet.filter(
        (tokenId) => !allV2Ids.includes(tokenId)
      );

      setTokenIds(availableBearsToClaim);
    }
  }, [allV2Ids, v1sInWallet, tokenIds]);

  if (v1sInWallet === undefined || price === undefined) {
    return (
      <Center h="52">
        <BoldSpinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <Container>
        <Panel>
          <Box mb="4" pl="2" mt="1.5">
            <Heading fontSize="2xl">
              {translate("v1s:title", {
                count: v1sInWallet.length,
                v1Name: plural(v1Name),
              })}
            </Heading>
            <Text fontSize="sm" mt="1" opacity="0.8">
              {translate("v1s:description", { v2Name: plural(v2Name) })}
            </Text>
          </Box>

          <SimpleGrid
            columns={{
              base: 2,
              sm: v1sInWallet.length > 1 ? 3 : 2,
            }}
            spacing="3"
            w="full"
          >
            {v1sInWallet
              .sort((a, b) => b - a)
              .map((tokenId) => {
                const isSelected = tokenIds?.includes(tokenId);
                const isMinted = allV2Ids.includes(tokenId);

                return (
                  <V1Card
                    key={tokenId}
                    tokenId={tokenId}
                    isSelected={tokenIds?.includes(tokenId)}
                    isDisabled={isMinted}
                    badgeText={
                      isMinted ? translate("common:minted") : undefined
                    }
                    onClick={() => {
                      if (isSelected) {
                        setTokenIds(tokenIds?.filter((id) => tokenId !== id));
                      } else {
                        setTokenIds([...(tokenIds || []), tokenId]);
                      }
                    }}
                  >
                    <Text fontWeight="600" px="1.5" pt="1.5">
                      #{tokenId}
                    </Text>
                  </V1Card>
                );
              })}

            <EmptyNftCardWithLink
              label={translate("common:buyOnOpenSea")}
              externalUrl={v1OpenSeaUrl}
            />
          </SimpleGrid>

          <Box pt="5" pb="2" px="1.5">
            <Button
              onClick={handleReadTerms}
              isDisabled={!tokenIds?.length}
              colorScheme={tokenIds?.length ? "primary" : "dark"}
              isLoading={isMinting}
              w="full"
            >
              {tokenIds !== undefined && tokenIds.length > 0
                ? tokenIds.length > 1
                  ? translate("v1s:mint:multi", {
                      count: tokenIds.length,
                      price: formatEtherBalance(totalPrice!),
                      currency: "ETH",
                      v2Name: pluralize(v2Name, tokenIds.length),
                    })
                  : translate("v1s:mint:one", {
                      price: formatEtherBalance(totalPrice!),
                      currency: "ETH",
                      v2Name: pluralize(v2Name, tokenIds.length),
                    })
                : translate("v1s:mint:none", {
                    v2Name: plural(v2Name),
                  })}
            </Button>
          </Box>
        </Panel>
      </Container>

      {tokenIds && address && isTermsConsentPopupOpen && (
        <TermsConsentPopup
          isOpen
          address={address}
          tokenIds={tokenIds}
          onSuccess={handleTermsConsentSuccess}
          onClose={setIsTermsConsentPopupOpen.off}
        />
      )}

      {tokenIds && isMintSuccessPopupOpen && (
        <MintSuccessPopup
          isOpen
          tokenIds={tokenIds}
          onSuccess={handleMintSuccess}
          onClose={setIsMintSuccessPopupOpen.off}
        />
      )}
    </>
  );
};

export default V1sContainer;
